import { Carousel } from "@mantine/carousel";
import { ActionIcon, Box, Grid, Group, ScrollArea } from "@mantine/core";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import React, { ReactNode, useEffect, useState } from "react";

import { useDragToScroll } from "@hooks";

import { Card, CardProps, TitleCard } from "@components";

import * as classes from "./CardScroll.module.css";

export { classes as cardScrollClasses };

export interface CardScrollProps<T = unknown> {
  sidebarContent: ReactNode;
  title: ReactNode;
  data: T[];
  renderItem: (item: T, index: number) => ReactNode;
  cardProps?: Partial<CardProps>;
  cardClassName?: string;
  sidebarMinWidth?: number;
}

export function CardScroll<T = unknown>({
  sidebarContent,
  title,
  data,
  renderItem,
  cardProps = {},
  cardClassName,
  sidebarMinWidth = 360,
}: CardScrollProps<T>) {
  const { viewportRef, dragHandlers, dragStyles } = useDragToScroll();
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const defaultCardProps: Partial<CardProps> = {
    miw: 480,
    maw: 480,
    p: "xs",
    h: "100%",
    ...cardProps,
  };

  const handleScrollPositionChange = (position: { x: number; y: number }) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const { scrollWidth, clientWidth } = viewport;
    const maxScroll = scrollWidth - clientWidth;
    const hasScroll = scrollWidth > clientWidth;

    setHasScrollbar(hasScroll);

    if (!hasScroll) {
      setShowLeftGradient(false);
      setShowRightGradient(false);
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const isAtStart = position.x <= 10;
    const isAtEnd = position.x >= maxScroll - 10;

    setShowLeftGradient(!isAtStart);
    setShowRightGradient(!isAtEnd);
    setCanScrollLeft(!isAtStart);
    setCanScrollRight(!isAtEnd);
  };

  const scrollLeft = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    // Get card width from props or use default
    const cardWidth = typeof cardProps.miw === "number" ? cardProps.miw : 480;
    const scrollAmount = cardWidth * 1.5; // Account for negative margin overlap
    viewport.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  };

  const scrollRight = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    // Get card width from props or use default
    const cardWidth = typeof cardProps.miw === "number" ? cardProps.miw : 480;
    const scrollAmount = cardWidth * 1.5; // Account for negative margin overlap
    viewport.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  useEffect(() => {
    // Initial check for scrollbar presence
    const viewport = viewportRef.current;
    if (viewport) {
      const hasScroll = viewport.scrollWidth > viewport.clientWidth;
      setHasScrollbar(hasScroll);
      setShowRightGradient(hasScroll);
      setCanScrollRight(hasScroll);
      setCanScrollLeft(false);
    }
  }, [data]);

  return (
    <Box>
      <Box hiddenFrom="lg">
        <TitleCard title={title} mb="xs" />
        <Carousel
          emblaOptions={{
            loop: true,
          }}
          height="auto"
          slideSize="auto"
          slideGap="xs"
          controlSize={48}
          nextControlIcon={<IconArrowRight size={24} />}
          previousControlIcon={<IconArrowLeft size={24} />}
          nextControlProps={{
            style: {
              borderRadius: "var(--mantine-radius-sm)",
              background: "transparent",
              boxShadow:
                "inset 0 1px 1px 0 rgb(255 255 255 / 25%), 0 1px 1px 0 rgb(0 0 0 / 25%)",
              backdropFilter: "blur(4px)",
              border: "none",
              opacity: 1,
              color: "#FFFFFF",
            },
          }}
          previousControlProps={{
            style: {
              borderRadius: "var(--mantine-radius-sm)",
              background: "transparent",
              boxShadow:
                "inset 0 1px 1px 0 rgb(255 255 255 / 25%), 0 1px 1px 0 rgb(0 0 0 / 25%)",
              backdropFilter: "blur(4px)",
              border: "none",
              opacity: 1,
              color: "#FFFFFF",
            },
          }}
        >
          {data.map((item, index) => (
            <Carousel.Slide key={index}>
              <Card {...cardProps} className={cardClassName}>
                {renderItem(item, index)}
              </Card>
            </Carousel.Slide>
          ))}

          {data.map((item, index) => (
            <Carousel.Slide key={`row2-${index}`}>
              <Card {...cardProps} className={cardClassName}>
                {renderItem(item, index)}
              </Card>
            </Carousel.Slide>
          ))}
        </Carousel>
      </Box>

      {/* Desktop Layout - horizontal scroll */}
      <Box visibleFrom="lg">
        <Grid gutter="xs">
          <Grid.Col span="content">
            <Card p="xxl" bg="layoutDark" h="100%" miw={sidebarMinWidth}>
              {sidebarContent}
            </Card>
          </Grid.Col>

          <Grid.Col span="auto" style={{ minWidth: 0 }} my={-64} pos="relative">
            {/* Right gradient */}
            <Box
              w={32}
              pos="absolute"
              top={16}
              bottom={16}
              right={0}
              bg="linear-gradient(
              to left,
              var(--mantine-color-layoutBody-0) 15%,
              transparent
            )"
              style={{
                zIndex: 3,
                opacity: hasScrollbar && showRightGradient ? 1 : 0,
                transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />

            {/* Left gradient */}
            <Box
              w={32}
              pos="absolute"
              top={16}
              bottom={16}
              left={0}
              bg="linear-gradient(
              to right,
              var(--mantine-color-layoutBody-0) 15%,
              transparent
            )"
              style={{
                zIndex: 3,
                opacity: hasScrollbar && showLeftGradient ? 1 : 0,
                transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />

            {/* Left arrow button */}
            <ActionIcon
              onClick={scrollLeft}
              size={48}
              radius="sm"
              pos="absolute"
              left={40}
              top="50%"
              style={{
                transform: "translateY(-50%)",
                zIndex: 4,
                background: "var(--mantine-color-brandPrimary-0)",
                boxShadow:
                  "inset 0 1px 1px 0 rgb(255 255 255 / 15%), 0 1px 1px 0 rgb(0 0 0 / 20%)",
                backdropFilter: "blur(4px)",
                border: "none",
                color: "#FFFFFF",
                opacity: canScrollLeft ? 1 : 0,
                pointerEvents: canScrollLeft ? "auto" : "none",
                transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <IconArrowLeft size={24} />
            </ActionIcon>

            {/* Right arrow button */}
            <ActionIcon
              onClick={scrollRight}
              size={48}
              radius="sm"
              pos="absolute"
              right={40}
              top="50%"
              style={{
                transform: "translateY(-50%)",
                zIndex: 4,
                background: "var(--mantine-color-brandPrimary-0)",
                boxShadow:
                  "inset 0 1px 1px 0 rgb(255 255 255 / 15%), 0 1px 1px 0 rgb(0 0 0 / 20%)",
                backdropFilter: "blur(4px)",
                border: "none",
                color: "#FFFFFF",
                opacity: canScrollRight ? 1 : 0,
                pointerEvents: canScrollRight ? "auto" : "none",
                transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <IconArrowRight size={24} />
            </ActionIcon>

            <ScrollArea
              viewportRef={viewportRef}
              type="hover"
              scrollbarSize={8}
              h="100%"
              onScrollPositionChange={handleScrollPositionChange}
              styles={{
                viewport: { height: "100%" },
                content: {
                  height: "100%",
                  padding: 64,
                  paddingLeft: 256,
                },
              }}
              {...dragHandlers}
              style={dragStyles}
            >
              <Group h="100%" wrap="nowrap" gap={0}>
                {data.map((item, index) => (
                  <Card
                    key={index}
                    {...defaultCardProps}
                    className={cardClassName}
                  >
                    {renderItem(item, index)}
                  </Card>
                ))}

                <Box>&nbsp;</Box>
              </Group>
            </ScrollArea>
          </Grid.Col>
        </Grid>
      </Box>
    </Box>
  );
}
